/**
 * Convex Report Builder Types
 *
 * Generic types for building reusable report configurations
 * that work with any Convex data source.
 */

// Filter operator types
export type FilterOperator
  = | 'equals'
    | 'not_equals'
    | 'contains'
    | 'not_contains'
    | 'starts_with'
    | 'ends_with'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'between'
    | 'in'
    | 'not_in'
    | 'is_null'
    | 'is_not_null';

// Field data types
export type FieldType = 'string' | 'number' | 'boolean' | 'date' | 'enum';

// Column configuration for the data grid preview
export interface ReportColumn<T = unknown> {
  field: keyof T & string;
  headerName: string;
  width?: number;
  minWidth?: number;
  flex?: number;
  sortable?: boolean;
  filter?: boolean;
  valueFormatter?: (value: unknown, row: T) => string;
  cellRenderer?: (value: unknown, row: T) => React.ReactNode;
}

// Enum option with optional styling
export interface EnumOption {
  value: string;
  label: string;
  // Optional icon component for styled chips
  icon?: React.ComponentType<{ className?: string }>;
  // Optional className for custom chip styling
  className?: string;
  // Optional selected className for when the chip is active
  selectedClassName?: string;
}

// Filter field configuration
export interface FilterField<T = unknown> {
  field: keyof T & string;
  label: string;
  type: FieldType;
  operators?: FilterOperator[];
  // For enum types, provide options
  enumOptions?: EnumOption[];
  // For grouped checkbox selection
  groupLabel?: string;
  // Default value
  defaultValue?: unknown;
  // Allow multiple selection (for enum types)
  multiple?: boolean;
}

// A single filter condition
export interface FilterCondition {
  id: string;
  field: string;
  operator: FilterOperator;
  value: unknown;
  enabled: boolean;
}

// Filter group (AND/OR)
export interface FilterGroup {
  id: string;
  logic: 'and' | 'or';
  conditions: FilterCondition[];
}

// Complete report configuration
export interface ReportConfig<T = unknown> {
  id?: string;
  name: string;
  description?: string;
  columns: ReportColumn<T>[];
  filterFields: FilterField<T>[];
  filters: FilterGroup;
  sortField?: keyof T & string;
  sortDirection?: 'asc' | 'desc';
  createdAt?: number;
  updatedAt?: number;
}

// Preset filter configuration (for quick apply)
export interface FilterPreset {
  id: string;
  name: string;
  description?: string;
  filters: FilterGroup;
}

// Export format options
export type ExportFormat = 'xlsx' | 'csv' | 'json';

// Export configuration
export interface ExportConfig {
  format: ExportFormat;
  filename: string;
  includeHeaders: boolean;
  selectedColumns?: string[];
}

// Props for the ConvexReportBuilder component
export interface ConvexReportBuilderProps<T> {
  // Data source
  data: T[] | undefined;
  isLoading: boolean;

  // Schema configuration
  columns: ReportColumn<T>[];
  filterFields: FilterField<T>[];

  // Optional presets
  presets?: FilterPreset[];

  // Optional saved reports (for persistence)
  savedReports?: ReportConfig<T>[];
  onSaveReport?: (config: ReportConfig<T>) => void;
  onDeleteReport?: (reportId: string) => void;

  // Title and description
  title: string;
  description?: string;

  // Export configuration
  defaultFilename?: string;

  // Optional row ID getter
  getRowId?: (row: T) => string;
}

// Helper to get default operators for a field type
export function getDefaultOperators(type: FieldType): FilterOperator[] {
  switch (type) {
    case 'string': {
      return ['equals', 'not_equals', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_null', 'is_not_null'];
    }
    case 'number': {
      return ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'between', 'is_null', 'is_not_null'];
    }
    case 'boolean': {
      return ['equals', 'not_equals'];
    }
    case 'date': {
      return ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'between', 'is_null', 'is_not_null'];
    }
    case 'enum': {
      return ['equals', 'not_equals', 'in', 'not_in'];
    }
    default: {
      return ['equals', 'not_equals'];
    }
  }
}

// Operator display labels
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: 'equals',
  not_equals: 'not equals',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  gt: 'greater than',
  gte: 'greater or equal',
  lt: 'less than',
  lte: 'less or equal',
  between: 'between',
  in: 'is any of',
  not_in: 'is none of',
  is_null: 'is empty',
  is_not_null: 'is not empty',
};
